import { EventBus } from "../eventbus";
import uniqid from "uniqid";
import i18n from "./i18next.js";
import { baseUrl } from "../config";
import axios from "axios";
import * as _ from "lodash";
import { Promise } from "es6-promise";
import { getFilenameAndExtension } from "./HelperFuncs";

const uploadLimit = 4000000000;

export const upload = (data, vueThis) => {
  const id = uniqid();
  const cancelTokenSource = axios.CancelToken.source();

  EventBus.$on(`snackbar--remove`, (data) => {
    if (id === data) {
      cancelTokenSource.cancel("Upload cancelled");
    }
  });

  EventBus.$emit(`snackbar--add`, {
    id,
    title: i18n.t("common.uploading"),
    noTimer: true,
    progress: 0,
  });

  const dataWithoutFiles = { ...data };
  delete dataWithoutFiles.files;

  if (calcFullSize(data.files) <= uploadLimit) {
    createFoldersAndFiles(
      createFolderTree(data.files),
      dataWithoutFiles,
      vueThis,
      id
    );
  }
};

const calcFullSize = (files) => {
  let fullSize = 0;

  files.forEach((file) => {
    fullSize += file.size;
  });

  return fullSize;
};

const createFoldersAndFiles = (obj, parentData, vueThis, id) => {
  const promises = [];
  let isDeepestFolder = false;

  if (Object.keys(obj).length) {
    let allFiles = true;

    Object.keys(obj).forEach((key) => {
      if (key.includes("my___file___")) {
        //is file
        promises.push(uploadFile({ ...parentData, ...obj[key] }, vueThis));
      } else {
        //is folder
        allFiles = false;

        //check for deeper folders and files
        if (Object.keys(obj[key]).length) {
          //has subitems so restart the loop when done here with these items

          promises.push(
            createFolder(
              parentData,
              key.replace("___", " "),
              obj[key],
              vueThis,
              id
            )
          );
        } else {
          isDeepestFolder = true;

          promises.push(
            createFolder(parentData, key.replace("___", " "), {}, vueThis, id)
          );
        }
      }
    });

    //all keys are files so after these uploads we are done
    isDeepestFolder = allFiles;
  } else {
    //was last item
    //get new files
    EventBus.$emit(`desktop-files-update`);

    EventBus.$emit(`snackbar--remove`, id);

    EventBus.$emit(`snackbar--add`, {
      id: uniqid(),
      title: i18n.t("common.upload_ready"),
      icon: "checkmark--round",
      color: "#82b347",
    });
  }

  if (isDeepestFolder) {
    Promise.all(promises)
      .then(() => {
        //this part is done

        //get new files
        EventBus.$emit(`desktop-files-update`);

        EventBus.$emit(`snackbar--remove`, id);

        EventBus.$emit(`snackbar--add`, {
          id: uniqid(),
          title: i18n.t("common.upload_ready"),
          icon: "checkmark--round",
          color: "#82b347",
        });
      })
      .catch((e) => {
        EventBus.$emit(`snackbar--remove`, id);

        if (e.response) {
          if (e.response.status == 402) {
            EventBus.$emit(`snackbar--add`, {
              id: uniqid(),
              icon: "error--round",
              color: "#df3535",
              title: i18n.t("upload.upload_limit_reached"),
            });
          } else {
            EventBus.$emit(`snackbar--add`, {
              id: uniqid(),
              icon: "error--round",
              color: "#df3535",
              title: i18n.t("common.upload_error"),
            });
          }
        } else {
          EventBus.$emit(`snackbar--add`, {
            id: uniqid(),
            icon: "error--round",
            color: "#df3535",
            title: i18n.t("common.upload_canceled"),
          });
        }
      });
  }
};

const createFolderTree = (files) => {
  if (files) {
    const folderTree = {};

    Array.from(files).forEach((file) => {
      const pathParts =
        file.webkitRelativePath !== ""
          ? file.webkitRelativePath.split("/")
          : file.fullPath
          ? file.fullPath.split("/")
          : [file.name];

      pathParts.forEach((part, index) => {
        let currentFullPath = [];

        if (index >= pathParts.length - 1) {
          //check if not hidden file
          if (part[0] !== ".") {
            currentFullPath = pathParts
              .filter((p, i) => i < index)
              .map((p) => p.replace(/ /, "___"));

            //is file
            const currentPath = _.get(folderTree, currentFullPath);
            if (currentPath) {
              _.set(
                folderTree,
                [...currentFullPath, `my___file___${uniqid()}`],
                {
                  file: file,
                  name: part,
                }
              );
            } else {
              _.set(folderTree, [`my___file___${uniqid()}`], {
                file: file,
                name: part,
              });
            }
          }
        } else {
          //is folder
          currentFullPath = pathParts
            .filter((p, i) => i <= index)
            .map((p) => p.replace(/ /, "___"));
          //check if folder is present
          if (!_.get(folderTree, currentFullPath)) {
            //is not present so create
            _.set(folderTree, currentFullPath, {});
          }
        }
      });
    });

    return folderTree;
  }

  return {};
};

const uploadFile = (data, vueThis) => {
  const formData = new FormData();

  formData.append("folder", data.folderId);

  if (data.tags) {
    formData.append("tags", data.tags);
  } else {
    formData.append("tags", []);
  }

  //update file name
  const parts = getFilenameAndExtension(data.file.name);

  formData.append(
    `files[]`,
    data.file,
    `${parts[0]}.${parts[1].toLowerCase()}`
  );

  formData.append(`myspace`, data.myspace);

  return vueThis.$axios.post(baseUrl + "/uploadfiles", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const createFolder = (parentData, folderName, obj, vueThis, id) => {
  const formData = new FormData();

  formData.append("foldername", folderName);
  formData.append("description", "");

  formData.append("private", 0);
  formData.append("folder", parentData.folderId);
  formData.append("myspace", parentData.myspace);

  return vueThis.$axios.post(baseUrl + "/folderadd", formData).then((r) => {
    setTimeout(() => {
      createFoldersAndFiles(
        obj,
        { ...parentData, folderId: r.data.folder_id },
        vueThis,
        id
      );
    }, 150);
  });
};
